import React, { useState, useEffect } from "react";
import {
  Button,
  Alert,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import styles from "./ContactDetails.module.css";
import Logo from "../components/Logo";
import SideBackground from "../components/SideBackground";
import api from "../API/axios";
import NotificationSnackbar from "../components/NotificationSnackbar";
import useNotification from "../hooks/useNotification";

export default function BypassCodes() {
  const [bypassCodes, setBypassCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const { notification, showNotification, hideNotification } =
    useNotification();

  useEffect(() => {
    // Generate bypass codes when component mounts
    generateBypassCodes();
  }, []);

  const generateBypassCodes = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const verificationToken = params.get("token");

      if (!verificationToken) {
        setError("Verification token missing.");
        setLoading(false);
        return;
      }

      const response = await api.post("/TwoFactor/generate-bypass-codes", {
        token: verificationToken,
      });
      // Defensive: support both old and new backend response structures
      const codes =
        response.data?.data?.bypassCodes || response.data?.bypassCodes || [];
      setBypassCodes(Array.isArray(codes) ? codes : []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Failed to generate bypass codes. Please try again.");
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const verificationToken = params.get("token");

      await api.post("/Users/complete-registration", {
        token: verificationToken,
      });

      showNotification(
        "Registration completed successfully! You can now log in.",
        "success"
      );
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      console.error(err);
      showNotification(
        "Failed to complete registration. Please try again.",
        "error"
      );
    }
  };

  const downloadCodes = () => {
    const codesText = bypassCodes.join("\n");
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bypass-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.contactDetailsRoot}>
      <div className={styles.contactDetailsLeft}>
        <div className={styles.contactDetailsLogoWrap}>
          <Logo />
        </div>
        <div
          className={styles.contactDetailsForm}
          style={{ marginTop: "80px" }}
        >
          <h2>Complete Setup</h2>
          <h4
            style={{
              textAlign: "center",
              fontWeight: 600,
              margin: "0px",
              marginTop: "-20px",
              color: "#888",
            }}
          >
            2 Factor Bypass <br /> Codes
          </h4>

          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              color: "#666",
            }}
          >
            <p>
              In the event that you are unable to access your chosen 2 factor
              method, you <br />
              are able to bypass the process by using one of the below code.{" "}
              <br />
              Each code is a one use only. If all codes are used, your account
              will be <br />
              inaccessible and you must contact your administrator to access
              your <br />
              account. <br />
              Please take a note of the codes and store them in a{" "}
              <strong>Secure location</strong>.
            </p>

            {/* {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )} */}
            {loading ? (
              <div
                style={{ textAlign: "center", padding: "20px", color: "#666" }}
              >
                Generating bypass codes...
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: 260,
                  margin: "0 auto",
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    marginBottom: "16px",
                    fontSize: "13px",
                  }}
                >
                  {bypassCodes.map((code, index) => (
                    <p
                      key={index}
                      style={{
                        fontWeight: "bold",
                        width: "120px",
                        margin: "8px",
                      }}
                    >
                      {code}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: 260,
              display: "flex",
              flexDirection: "column",
              margin: "0 auto",
            }}
          >
            <Typography
              variant="body1"
              align="center"
              onClick={downloadCodes}
              sx={{
                fontWeight: 700,
                color: "var(--color-primary)",
                cursor: "pointer",
              }}
            >
              Download Codes
            </Typography>
          </div>
          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              marginBottom: 12,
              color: "#333",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: 700,
            }}
          >
            <Checkbox size="small" onClick={() => setChecked(!checked)} />
            <p>
              I have taken note of my bypass codes and acknowledge
              <br />
              that I will not have access to them once leaving this page
            </p>
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: 260,
              display: "flex",
              flexDirection: "column",
              margin: "0 auto",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              size="large"
              onClick={handleComplete}
              disabled={loading || !checked}
              sx={{
                width: "100%",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
            >
              Complete Registration
            </Button>
          </div>
        </div>
      </div>
      <SideBackground>
        <div className={styles.contactDetailsBgOverlay}></div>
      </SideBackground>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
